import { useRef, useCallback } from 'react';

/**
 * Hook para manejar sonidos diferentes según el cambio de estado
 *
 * Sonidos:
 * - NEW_ORDER: Nuevo pedido recibido (campana)
 * - TO_PREPARING: Pedido pasa a preparación (inicio cocina)
 * - TO_READY: Pedido listo para servir (ding exitoso)
 * - TO_DELIVERED: Pedido entregado (confirmación sutil)
 */

// URLs de sonidos (usando sonidos web gratuitos o archivos locales)
// En producción, estos archivos estarían en /public/sounds/
const SOUND_URLS = {
  new_order: `${process.env.PUBLIC_URL}/sounds/new-order.mp3`,
  to_preparing: `${process.env.PUBLIC_URL}/sounds/preparing.mp3`,
  to_ready: `${process.env.PUBLIC_URL}/sounds/ready.mp3`,
  to_delivered: `${process.env.PUBLIC_URL}/sounds/delivered.mp3`,
};

// Fallback: usar Web Audio API para generar sonidos básicos si no hay archivos
const generateTone = (frequency, duration, type = 'sine') => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  } catch (error) {
    console.log('Audio not supported:', error);
  }
};

// Configuración de sonidos por tipo
const SOUND_CONFIG = {
  new_order: { freq: 880, duration: 0.3, type: 'sine', repeat: 2 },      // A5 - alerta
  to_preparing: { freq: 523, duration: 0.15, type: 'square' },          // C5 - inicio
  to_ready: { freq: 1047, duration: 0.2, type: 'sine', repeat: 2 },     // C6 - exitoso
  to_delivered: { freq: 392, duration: 0.1, type: 'triangle' },         // G4 - confirmación
};

function useStatusSounds() {
  const audioRefs = useRef({});

  // Preload audio files
  const preloadSounds = useCallback(() => {
    Object.entries(SOUND_URLS).forEach(([key, url]) => {
      const audio = new Audio();
      audio.preload = 'auto';
      audio.src = url;
      audioRefs.current[key] = audio;
    });
  }, []);

  // Play sound with fallback to generated tones
  const playSound = useCallback((type) => {
    // Try to play audio file first
    const audio = audioRefs.current[type];
    if (audio) {
      audio.currentTime = 0;
      audio.play().catch(() => {
        // Fallback to generated tone
        const config = SOUND_CONFIG[type];
        if (config) {
          if (config.repeat) {
            for (let i = 0; i < config.repeat; i++) {
              setTimeout(() => {
                generateTone(config.freq, config.duration, config.type);
              }, i * 200);
            }
          } else {
            generateTone(config.freq, config.duration, config.type);
          }
        }
      });
    } else {
      // No audio file loaded, use generated tone
      const config = SOUND_CONFIG[type];
      if (config) {
        if (config.repeat) {
          for (let i = 0; i < config.repeat; i++) {
            setTimeout(() => {
              generateTone(config.freq, config.duration, config.type);
            }, i * 200);
          }
        } else {
          generateTone(config.freq, config.duration, config.type);
        }
      }
    }
  }, []);

  // Play sound for new order
  const playNewOrderSound = useCallback(() => {
    playSound('new_order');
  }, [playSound]);

  // Play sound for status change
  const playStatusChangeSound = useCallback((fromStatus, toStatus) => {
    switch (toStatus) {
      case 'preparing':
        playSound('to_preparing');
        break;
      case 'ready':
        playSound('to_ready');
        break;
      case 'delivered':
        playSound('to_delivered');
        break;
      default:
        break;
    }
  }, [playSound]);

  return {
    preloadSounds,
    playNewOrderSound,
    playStatusChangeSound,
    playSound,
  };
}

export default useStatusSounds;
